/* ============================================================================
 * TeamSolutions, structural reference: ClickUp's "AI solutions for every
 * team" section (centered title, pill-tab strip, two-column panel).
 *
 * Framing: "if you're in <department>, here's how Intellsys OS helps you."
 * Each tab pulls 3–4 apps from across the 24-app catalog, apps are NOT
 * constrained to a single category. AI Agent shows up multiple times by
 * design, agents serve every team.
 *
 *  - Left column: outcome-style 2-line heading (dark top + gradient bottom),
 *    short description, 3 checkmark bullets, gradient "Explore →" CTA.
 *  - Right column: app cards, icon swatch, name, 1-line description pulled
 *    from _appsData.ts via APP_LOOKUP (single source of truth).
 *
 * Sizing note: panel heading is text-heading3 (36px lg), not heading2-25,
 * because the half-column at lg breakpoints can't fit 48px without wrapping
 * awkwardly. Section title above stays heading2-25.
 *
 * NOTE: do NOT use Tailwind's `max-w-md` etc. here, `--spacing-md` resolves
 * those to 1.125rem (18px), squishing the column to single-word-per-line.
 * Use explicit arbitrary values like `max-w-[28rem]`.
 * ============================================================================ */

import {useState} from "react";
import {Check} from "react-bootstrap-icons";
import {Link} from "react-router";

import {appCategories} from "./_appsData";
import {APP_ICONS, FallbackIcon, type IconComp} from "./_appIcons";
import {CtaButtonFilled} from "./ctaButtonFilled";

type TabKey = "sales" | "marketing" | "operations" | "customer-service" | "people" | "leadership";

interface TabContent {
    slug: TabKey;
    label: string;
    headingTop: string;
    headingBottom: string;
    description: string;
    bullets: ReadonlyArray<string>;
    appSlugs: ReadonlyArray<string>;
}

const TABS: ReadonlyArray<TabContent> = [
    {
        slug: "sales",
        label: "Sales",
        headingTop: "Close more deals,",
        headingBottom: "everywhere they buy.",
        description:
            "From the first touch to the repeat order, direct, online, channel and field. One platform for every lead, every quotation, every rupee.",
        bullets: [
            "Lead-to-cash on a single platform",
            "Direct, online and channel sales together",
            "Quotations, orders and incentives without spreadsheets",
        ],
        appSlugs: ["crm", "sales", "field-sales", "ai-agent"],
    },
    {
        slug: "marketing",
        label: "Marketing",
        headingTop: "Run every campaign,",
        headingBottom: "measure every rupee.",
        description:
            "Plan, launch and attribute multi-channel campaigns, from your website to WhatsApp to Meta ads, all stitched to actual revenue.",
        bullets: [
            "Multi-channel outbound automation",
            "Owned content, brand and demand in one place",
            "Cross-channel attribution back to revenue",
        ],
        appSlugs: ["campaigns", "content", "website", "analytics"],
    },
    {
        slug: "operations",
        label: "Operations",
        headingTop: "Move product,",
        headingBottom: "not paper.",
        description:
            "Stock, invoices, fulfilment and field repair, on the same platform that captured the sale. No reconciliation between sales and ops.",
        bullets: [
            "Stock, invoicing and fulfilment on one ledger",
            "Dispatch technicians and track repair",
            "Native connectors to ad platforms and marketplaces",
        ],
        appSlugs: ["inventory", "invoicing", "field-service", "connector"],
    },
    {
        slug: "customer-service",
        label: "Customer Service",
        headingTop: "Resolve faster,",
        headingBottom: "wherever they reach you.",
        description:
            "Multi-channel support, WhatsApp, email, chat, calls and on-the-ground service, backed by every order and ticket in one history.",
        bullets: [
            "Tickets from every channel in one queue",
            "Two-way conversations with full context",
            "Field service dispatched from the same ticket",
        ],
        appSlugs: ["helpdesk", "chat", "warranty", "ai-agent"],
    },
    {
        slug: "people",
        label: "People & HR",
        headingTop: "Hire and run a team,",
        headingBottom: "without the friction.",
        description:
            "Attendance, hiring, learning, sign-offs and documents, back-office plumbing so the rest of the business can move fast.",
        bullets: ["Attendance, shifts and leave in one place", "Hire, onboard and train your team", "Documents and approvals routed cleanly"],
        appSlugs: ["attendance", "recruitment", "learning", "documents"],
    },
    {
        slug: "leadership",
        label: "Founders",
        headingTop: "See the whole business,",
        headingBottom: "in a single view.",
        description:
            "Real-time dashboards across every app, sales, marketing, fulfilment, ops, with AI agents that surface what actually needs you.",
        bullets: [
            "Real-time signal across every function",
            "Agents that flag what needs your attention",
            "No more end-of-month reconciliation",
        ],
        appSlugs: ["analytics", "ai-agent", "connector"],
    },
];

// Slug-keyed lookup so the right column renders names + descriptions straight
// from _appsData.ts (single source of truth, no duplicated copy here).
const APP_LOOKUP = new Map<string, {name: string; description: string}>();
appCategories.forEach((cat) => {
    cat.apps.forEach((app) => APP_LOOKUP.set(app.slug, {name: app.name, description: app.description}));
});

export function TeamSolutions() {
    const [activeSlug, setActiveSlug] = useState<TabKey>("sales");
    const active = TABS.find((t) => t.slug === activeSlug) ?? TABS[0];
    if (active == null) return null;

    return (
        <section
            aria-labelledby="solutions-title"
            className="gj-px-screen-edge-constrained gj-py-section-divider"
        >
            {/* Section header */}
            <div className="text-left lg:text-center mb-md3">
                <h2
                    id="solutions-title"
                    className="text-heading2-25 text-text-primary"
                >
                    Built for <span className="text-gradient-blue">every team</span>.
                </h2>
            </div>

            {/* Pill tabs, horizontal scroll on phone so they never wrap, wrap on desktop.
             * Edge fades hint the strip is scrollable. */}
            <div className="relative mb-md3 -mx-5 lg:mx-0">
                <div className="flex lg:flex-wrap lg:justify-center gap-2 md:gap-2.5 overflow-x-auto lg:overflow-visible px-5 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {TABS.map((tab) => {
                        const isActive = tab.slug === activeSlug;
                        return (
                            <button
                                key={tab.slug}
                                type="button"
                                onClick={() => {
                                    setActiveSlug(tab.slug);
                                }}
                                // Same chip shape/classes as hero.tsx FeatureChip, kept in sync intentionally.
                                className={[
                                    "text-nav-text-b px-2 py-1 lg:px-3 lg:py-2 rounded-sm lg:rounded-lg border transition-colors duration-200 whitespace-nowrap inline-flex items-center gap-1.5 shrink-0",
                                    isActive
                                        ? "border-primary-500 text-primary-500 bg-primary-200/40 hover:bg-primary-200/60"
                                        : "border-surface-lines-and-borders text-text-body bg-transparent hover:bg-surface-surface-hover",
                                ].join(" ")}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Active panel. On phone the panel bleeds out of the section rail (negative margin
             * matches `--gj-px-screen-edge-constrained`) so it spans edge-to-edge, while the
             * heading + tab strip above stay aligned with the rail. Inner padding (`p-sm2`)
             * keeps cards off the very edge. From lg, the bleed is removed and the panel
             * groups the two columns inside the rail. */}
            <div className="mx-[calc(var(--gj-px-screen-edge-constrained)*-1)] lg:mx-0 grid grid-cols-1 lg:grid-cols-2 gap-md lg:gap-md3 bg-surface-secondary-background border border-surface-lines-and-borders/60 rounded-sm lg:rounded-lg p-sm2 lg:p-md3">
                {/* Left column */}
                <div className="flex flex-col gap-sm2 min-w-0">
                    <h3 className="text-main-title leading-tight">
                        <span className="block text-text-primary">{active.headingTop}</span>
                        <span className="block text-gradient-blue">{active.headingBottom}</span>
                    </h3>

                    <p className="text-medium text-text-body max-w-[28rem]">{active.description}</p>

                    <ul className="flex flex-col gap-2 mt-1">
                        {active.bullets.map((b) => (
                            <li
                                key={b}
                                className="flex items-start gap-2 text-medium text-text-primary"
                            >
                                <span className="cta-bg-gradient text-[#FFFFFF] rounded-full w-4 h-4 flex items-center justify-center shrink-0 mt-1">
                                    <Check size={10} />
                                </span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>

                    <CtaButtonFilled
                        className="self-start mt-sm2"
                        href="/apps"
                        text={`Explore for ${active.label}`}
                        arrow
                    />
                </div>

                {/* Right column, app cards */}
                <div className="flex flex-col gap-2 min-w-0">
                    {active.appSlugs.map((slug) => {
                        const app = APP_LOOKUP.get(slug);
                        if (app == null) return null;
                        const Icon: IconComp = APP_ICONS[slug] ?? FallbackIcon;
                        return (
                            <Link
                                key={slug}
                                to="/apps"
                                className="bg-[#FFFFFF] rounded-sm lg:rounded-lg p-sm2 flex items-start gap-sm2 border border-surface-lines-and-borders/50 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_12px_28px_-12px_rgba(31,64,203,0.25)] hover:-translate-y-0.5 transition-all"
                            >
                                <span className="cta-bg-gradient text-[#FFFFFF] w-9 h-9 rounded-sm lg:rounded-lg flex items-center justify-center shrink-0">
                                    <Icon size={16} />
                                </span>
                                <span className="flex flex-col gap-0.5 min-w-0">
                                    <span className="text-medium-b text-text-primary">{app.name}</span>
                                    <span className="text-nav-text text-text-body line-clamp-1">{app.description}</span>
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
